import { useEffect, useMemo, useRef } from 'react';
import type { AnyExtension } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import { Markdown } from '@tiptap/markdown';
import { RichTextProvider } from 'reactjs-tiptap-editor';
import 'reactjs-tiptap-editor/style.css';
import 'react-image-crop/dist/ReactCrop.css';
import { useResolvedTheme } from '../use-theme';
import { cn } from '../../lib/utils';
import { richTextBaseExtensions } from '../work/rich-text-kit';
import { MermaidDiagram } from './mermaid-diagram';
import { WorkItemEmbed } from './work-item-embed';

type Props = {
  /** Markdown source (wiki.md W-5). */
  value: string;
  className?: string;
};

function noUpload(): Promise<string> {
  return Promise.reject(new Error('Images cannot be uploaded from a rendered page'));
}

/**
 * Renders wiki Markdown with the same schema as work-item rich text (wiki.md W-5), plus
 * work-item cards and Mermaid diagrams. Read-only: editing happens in Markdown source.
 */
export default function WikiMarkdownView({ value, className }: Props) {
  const theme = useResolvedTheme();
  const extensions = useMemo<AnyExtension[]>(
    () => [
      ...richTextBaseExtensions({ placeholder: '', upload: noUpload, underline: false }),
      WorkItemEmbed,
      MermaidDiagram,
      Markdown.configure({ indentation: { style: 'space', size: 2 } }),
    ],
    [],
  );

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    extensions,
    content: value,
    contentType: 'markdown',
    editable: false,
    editorProps: {
      // `!` beats the library's high-specificity `.ProseMirror` padding rule.
      attributes: { class: 'outline-none !p-0' },
    },
  });

  // Live preview: re-parse when the source changes (the initial value went in through `content`).
  const appliedRef = useRef(value);
  useEffect(() => {
    if (!editor || editor.isDestroyed || appliedRef.current === value) return;
    appliedRef.current = value;
    editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
  }, [editor, value]);

  if (!editor) {
    return <div className={cn('min-h-[8rem]', className)} data-editor="true" />;
  }

  return (
    <div data-editor="true" className={cn('wiki-body text-foreground', className)}>
      <RichTextProvider editor={editor} dark={theme === 'dark'}>
        <EditorContent editor={editor} />
      </RichTextProvider>
    </div>
  );
}
